import { useEffect, useMemo, useRef, useState } from "react";
import { IconChart, IconDoc, IconGlobe, IconLightbulb, IcCap, IcFlag, IcArrow, Wheat } from "./icon2";

/* ==================================================================
   ASSOCHAM · SECTORS MAIN PAGE  —  SINGLE COMPONENT FILE
   Palette: #DA2128 #DB2628 #DB6400 #F59C00 | #FAF7F2 #FEF7F4 #FFFFFF
   Sab kuch isi file me: styles, icons, data, sections, footer.
================================================================== */

/* ------------------------------ DATA ------------------------------ */

const IMAGES = {
  hero: "https://images.pexels.com/photos/20445169/pexels-photo-20445169.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=900&w=1600",
  field: "https://images.pexels.com/photos/34479827/pexels-photo-34479827.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=800&w=1200",
  meeting: "https://images.pexels.com/photos/6949940/pexels-photo-6949940.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=800&w=1200",
  drone: "https://images.pexels.com/photos/34182370/pexels-photo-34182370.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=900&w=1300",
  wheat: "https://images.pexels.com/photos/12656497/pexels-photo-12656497.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=800&w=1200",
  sower: "https://images.pexels.com/photos/26762735/pexels-photo-26762735.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=800&w=1200",
};

const SECTORS = [
  {
    id: "agriculture-food-processing",
    label: "Agriculture & Food Processing",
    icon: "wheat",
    tagline: "Farm to fork value creation",
    blurb:
      "Driving farm productivity, agri-inputs, food processing and value addition to make Indian agriculture globally competitive.",
    metric: "₹40 Lakh Cr",
    metricLabel: "Sector output",
    featured: true,
    councils: [
      {
        label: "Agri Industries & Farming Practices",
        href: "#council",
        desc: "Policy advocacy, precision farming and farmer prosperity.",
        live: true,
      },
      {
        label: "Food Processing & Value Addition",
        href: "#contact",
        desc: "Cold chains, mega food parks and processed exports.",
      },
    ],
  },
  {
    id: "agri-tech-innovation",
    label: "Agri-Tech & Innovation",
    icon: "drone",
    tagline: "Digital farms, data-led decisions",
    blurb:
      "Scaling drones, IoT, satellite intelligence and startup innovation to bridge the 86% agri-tech divide.",
    metric: "5,000+",
    metricLabel: "Agri startups",
    councils: [
      {
        label: "Precision Farming & Drones",
        href: "#agri-tech",
        desc: "Kisan drones, sensors and satellite-based advisory.",
      },
      {
        label: "Agri Startups & Digital Platforms",
        href: "#agri-tech",
        desc: "Funding, incubation and market-linkage platforms.",
      },
    ],
  },
  {
    id: "rural-allied",
    label: "Rural & Allied Sectors",
    icon: "drop",
    tagline: "Dairy, fisheries & water security",
    blurb:
      "Strengthening allied livelihoods — dairy, fisheries, animal husbandry — and efficient irrigation across rural India.",
    metric: "230 MT",
    metricLabel: "Milk production",
    councils: [
      {
        label: "Dairy, Fisheries & Animal Husbandry",
        href: "#stakeholders",
        desc: "Productivity, breed improvement and cold logistics.",
      },
      {
        label: "Water Management & Irrigation",
        href: "#focus-areas",
        desc: "Micro-irrigation, watershed and rainwater harvesting.",
      },
    ],
  },
  {
    id: "sustainability-climate",
    label: "Sustainability & Climate",
    icon: "leaf",
    tagline: "Green, resilient, regenerative",
    blurb:
      "Natural farming, carbon markets, soil health and climate-resilient cropping systems for a net-zero agri economy.",
    metric: "2070",
    metricLabel: "Net-zero target",
    councils: [
      {
        label: "Natural & Organic Farming",
        href: "#events",
        desc: "Zero-budget natural farming and certification.",
      },
      {
        label: "Carbon, Bio-Energy & Circular Agri",
        href: "#publications",
        desc: "Bio-CNG, ethanol and crop-residue valorisation.",
      },
    ],
  },
  {
    id: "agri-trade-exports",
    label: "Agri Trade & Exports",
    icon: "globe",
    tagline: "Global markets for Indian produce",
    blurb:
      "Boosting agri exports through standards, market access, branding and cross-border trade facilitation.",
    metric: "$50 Bn",
    metricLabel: "Export ambition",
    councils: [
      {
        label: "Horticulture & Processed Exports",
        href: "#publications",
        desc: "APEDA linkages, quality standards and logistics.",
      },
      {
        label: "International Agri Collaboration",
        href: "#functions",
        desc: "Technology transfer and bilateral partnerships.",
      },
    ],
  },
  {
    id: "farm-machinery-inputs",
    label: "Farm Machinery & Inputs",
    icon: "tractor",
    tagline: "Mechanised, input-efficient farms",
    blurb:
      "Custom hiring centres, equipment banks, fertilisers, crop protection and seed innovation for higher yields.",
    metric: "47%",
    metricLabel: "Mechanisation level",
    councils: [
      {
        label: "Farm Mechanisation & Equipment",
        href: "#focus-areas",
        desc: "Tractors, harvesters and custom hiring models.",
      },
      {
        label: "Seeds, Fertilisers & Crop Protection",
        href: "#stakeholders",
        desc: "Regulatory reform and responsible input use.",
      },
    ],
  },
];

const NAV_ITEMS = [
  { label: "Home", href: "#home", type: "link" },
  {
    label: "Sectors",
    href: "#sector-directory",
    type: "nested",
    items: SECTORS.slice(0, 4).map((s) => ({
      label: s.label,
      href: `#sector-${s.id}`,
      councils: s.councils,
    })),
  },
  { label: "Council", href: "#council", type: "link" },
  { label: "Events", href: "#events", type: "link" },
  { label: "Publications", href: "#publications", type: "link" },
  { label: "Contact", href: "#contact", type: "link" },
];

const HERO_STATS = [
  { value: "12", label: "National Councils", icon: "users" },
  { value: "6", label: "Sector Verticals", icon: "target" },
  { value: "450+", label: "Industry Members", icon: "handshake" },
  { value: "28", label: "States Covered", icon: "globe" },
];

const COUNCIL_STATS = [
  { value: "40%", label: "of India's population dependent on agriculture" },
  { value: "200+", label: "stakeholders connected at AgriBiz Connect 2025" },
  { value: "86%", label: "farmers set to benefit from unified agri-tech" },
  { value: "3rd", label: "India – global rank in agricultural output" },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    icon: "chat",
    title: "Convene",
    text: "Industry leaders, ministries, research bodies and farmer organisations on one council table.",
  },
  {
    step: "02",
    icon: "book",
    title: "Research",
    text: "Evidence built with EY, PwC, KPMG and BDO into actionable position papers.",
  },
  {
    step: "03",
    icon: "policy",
    title: "Advocate",
    text: "Recommendations carried to policymakers through memoranda, consultations and task forces.",
  },
  {
    step: "04",
    icon: "sprout",
    title: "Implement",
    text: "Pilots, training and technology deployment take outcomes down to the last farm.",
  },
];

const CORE_FUNCTIONS = [
  {
    icon: "policy",
    title: "Public Policy Advocacy",
    text: "Engaging with policymakers to shape farmer-centric, growth-oriented agricultural policies and regulatory frameworks.",
  },
  {
    icon: "bulb",
    title: "Promotion of Innovation",
    text: "Fostering agri-tech adoption, precision farming, and R&D partnerships between industry, academia, and startups.",
  },
  {
    icon: "book",
    title: "Training & Research",
    text: "Conducting capacity-building programmes, field research, and knowledge dissemination for sustainable agriculture.",
  },
  {
    icon: "expo",
    title: "Exhibitions & Conferences",
    text: "Organizing national summits, trade fairs, and expositions to connect stakeholders across the agri value chain.",
  },
  {
    icon: "chat",
    title: "Workshops & Panel Discussions",
    text: "Hosting roundtables, seminars, and panel discussions on pressing issues facing Indian agriculture today.",
  },
  {
    icon: "globe",
    title: "International Collaboration",
    text: "Facilitating global partnerships for technology transfer, export promotion, and cross-border agri-trade.",
  },
];

const FOCUS_AREAS = [
  {
    icon: "leaf",
    title: "Climate-Resilient Farming",
    text: "Promoting drought-resistant crops, precision irrigation, and adaptive practices to combat climate change impacts on agriculture.",
  },
  {
    icon: "drone",
    title: "Precision Agriculture",
    text: "Advancing drone technology, IoT sensors, satellite imaging, and data-driven farm management for increased productivity.",
  },
  {
    icon: "sprout",
    title: "Sustainable Practices",
    text: "Advocating organic farming, natural farming, crop rotation, and integrated pest management for long-term soil health.",
  },
  {
    icon: "drop",
    title: "Water Management",
    text: "Driving adoption of micro-irrigation, rainwater harvesting, and efficient water-use techniques across farmlands.",
  },
  {
    icon: "tractor",
    title: "Farm Mechanization",
    text: "Encouraging modern machinery, equipment banks, and custom hiring centers to reduce drudgery and improve efficiency.",
  },
  {
    icon: "chain",
    title: "Value Chain & Market Linkages",
    text: "Strengthening post-harvest infrastructure, cold chains, food processing, and direct market access for farmers.",
  },
];

const FUNCTIONS = [
  { Icon: IconDoc, title: "Public Policy Advocacy", desc: "Engaging with policymakers to shape farmer-centric, growth-oriented agricultural policies and regulatory frameworks." },
  { Icon: IconLightbulb, title: "Promotion of Innovation", desc: "Fostering agri-tech adoption, precision farming, and R&D partnerships between industry, academia, and startups." },
  { Icon: IcCap, title: "Training & Research", desc: "Conducting capacity-building programmes, field research, and knowledge dissemination for sustainable agriculture." },
  { Icon: IcFlag, title: "Exhibitions & Conferences", desc: "Organizing national summits, trade fairs, and expositions to connect stakeholders across the agri value chain." },
  { Icon: IconChart, title: "Workshops & Panel Discussions", desc: "Hosting roundtables, seminars, and panel discussions on pressing issues facing Indian agriculture today." },
  { Icon: IconGlobe, title: "International Collaboration", desc: "Facilitating global partnerships for technology transfer, export promotion, and cross-border agri-trade." },
];

const STAKEHOLDERS = [
  { short: "MoA", name: "Ministry of Agriculture & Farmers Welfare" },
  { short: "MoFAHD", name: "Ministry of Fisheries, Animal Husbandry & Dairying" },
  { short: "MoJS", name: "Ministry of Jal Shakti" },
  { short: "MoCF", name: "Ministry of Chemicals & Fertilizers" },
  { short: "SFAC", name: "Small Farmers Agribusiness Consortium" },
  { short: "APEDA", name: "Agricultural & Processed Food Products Export Development Authority" },
  { short: "NDDB", name: "National Dairy Development Board" },
  { short: "ICAR", name: "Indian Council of Agricultural Research" },
  { short: "CSIR", name: "Council of Scientific & Industrial Research" },
  { short: "NCCD", name: "National Centre for Cold-chain Development" },
  { short: "MPEDA", name: "Marine Products Export Development Authority" },
];

const EVENTS = [
  {
    tag: "Summit",
    date: "August 2025",
    title: "AgriBiz Connect 2025",
    place: "Hyderabad, Telangana",
    text: 'Summit on Agriculture & Food Processing bringing together 200+ stakeholders under the theme "Vision for Inclusive Agri-Industrial Growth".',
    highlight:
      "India-Israel collaboration spotlight on precision farming and post-harvest technologies.",
  },
  {
    tag: "Conference",
    date: "October 2024",
    title: "National Conference on Climate Resilient Farming Solutions",
    place: "New Delhi",
    text: "Inaugurated by Shri Ramnath Thakur, Hon'ble MoS for Agriculture & Farmers Welfare, focusing on scaling precision farming for sustainable growth.",
    highlight:
      "Released joint ASSOCHAM-EY report on Agri-tech adoption for climate-resilient agriculture.",
  },
  {
    tag: "Workshop",
    date: "Ongoing",
    title: "Workshop Series on Natural Farming Practices",
    place: "Pan-India",
    text: "Training programmes in partnership with ICAR and state agricultural universities promoting zero-budget natural farming.",
    highlight: "Over 5,000 farmers trained across 12 states in FY 2024-25.",
  },
];

const PUBLICATIONS = [
  {
    date: "October 2024",
    partner: "With EY LLP",
    title: "Agri-tech Adoption for Climate-Resilient Agriculture in India",
    text: "A strategic roadmap for prioritizing climate-resilient practices and bridging the tech divide for 86% of Indian farmers.",
  },
  {
    date: "2024",
    partner: "Knowledge Partner",
    title: "Precision Farming: Scaling Sustainable Growth",
    text: "Policy recommendations on crop insurance, bio-CNG, ethanol production, and converting agricultural waste into energy.",
  },
  {
    date: "2025",
    partner: "Research Report",
    title: "Horticulture-Led Growth for Farmer Incomes",
    text: "Analysis of horticulture as a strategic lever for enhancing farmer incomes, food security, and rural employment.",
  },
];

const MEMBERSHIP_BENEFITS = [
  "Direct access to ministry-level consultations and policy drafting groups",
  "Speaking slots at national summits, expos and international delegations",
  "Co-branding on ASSOCHAM knowledge reports and research studies",
  "Curated B2B matchmaking across the agri and food value chain",
  "Early access to scheme intelligence, tenders and incentive programmes",
  "Participation in state-level chapters and regional working groups",
];

const INTERESTS = [
  "Partnership & Membership",
  "Policy Advocacy",
  "Speaking / Event Participation",
  "Research Collaboration",
  "Agri-Tech Startup Showcase",
  "Media & Press",
];

/* ------------------------------ ICONS ------------------------------ */

const ICON_PATHS = {
  policy: (
    <>
      <path d="M4 21V8l8-5 8 5v13" />
      <path d="M9 21v-6h6v6" />
      <path d="M2 21h20" />
    </>
  ),
  bulb: (
    <>
      <path d="M9 18h6M10 22h4" />
      <path d="M12 2a7 7 0 0 0-4 12.7V18h8v-3.3A7 7 0 0 0 12 2Z" />
    </>
  ),
  book: (
    <>
      <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5Z" />
      <path d="M4 17.5h16M9 7h7" />
    </>
  ),
  expo: (
    <>
      <path d="M3 21V9l9-6 9 6v12" />
      <path d="M3 13h18M8 21v-4h8v4" />
    </>
  ),
  chat: (
    <>
      <path d="M21 15a3 3 0 0 1-3 3H8l-5 4V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3Z" />
      <path d="M8 9h8M8 13h5" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18Z" />
    </>
  ),
  leaf: (
    <>
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8c0 5.5-4.78 10-10 10Z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </>
  ),
  drone: (
    <>
      <rect x="9" y="9" width="6" height="6" rx="1.5" />
      <path d="M9 9 5.5 5.5M15 9l3.5-3.5M9 15l-3.5 3.5M15 15l3.5 3.5" />
      <circle cx="4" cy="4" r="2" />
      <circle cx="20" cy="4" r="2" />
      <circle cx="4" cy="20" r="2" />
      <circle cx="20" cy="20" r="2" />
    </>
  ),
  sprout: (
    <>
      <path d="M12 22V11" />
      <path d="M12 11C12 7 9 5 5 5c0 4 3 6 7 6Z" />
      <path d="M12 13c0-3.5 2.5-5.5 6-5.5c0 3.5-2.5 5.5-6 5.5Z" />
      <path d="M8 22h8" />
    </>
  ),
  drop: (
    <>
      <path d="M12 2.5S5.5 9.5 5.5 14a6.5 6.5 0 0 0 13 0C18.5 9.5 12 2.5 12 2.5Z" />
      <path d="M9 15a3 3 0 0 0 3 3" />
    </>
  ),
  tractor: (
    <>
      <path d="M4 15V6h6l2 5h4" />
      <circle cx="7" cy="17" r="3.5" />
      <circle cx="18" cy="17" r="2.5" />
      <path d="M16 11h4v4" />
    </>
  ),
  chain: (
    <>
      <path d="M10 13a5 5 0 0 0 7.1.1l2.8-2.8a5 5 0 0 0-7.1-7.1L11 4.9" />
      <path d="M14 11a5 5 0 0 0-7.1-.1l-2.8 2.8a5 5 0 0 0 7.1 7.1L13 19.1" />
    </>
  ),
  mail: (
    <>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <path d="m3 7 9 6 9-6" />
    </>
  ),
  phone: (
    <path d="M6.6 2.5h-2A2.1 2.1 0 0 0 2.5 4.9C3 12.9 11.1 21 19.1 21.5a2.1 2.1 0 0 0 2.4-2.1v-2a1.8 1.8 0 0 0-1.4-1.7l-2.8-.7a1.7 1.7 0 0 0-1.7.6l-1 1.2a14.5 14.5 0 0 1-5.4-5.4l1.2-1a1.7 1.7 0 0 0 .6-1.7l-.7-2.8a1.8 1.8 0 0 0-1.7-1.4Z" />
  ),
  pin: (
    <>
      <path d="M12 22s7-6.2 7-12A7 7 0 0 0 5 10c0 5.8 7 12 7 12Z" />
      <circle cx="12" cy="10" r="2.6" />
    </>
  ),
  download: (
    <>
      <path d="M12 3v12" />
      <path d="m7 11 5 5 5-5" />
      <path d="M4 20h16" />
    </>
  ),
  arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  check: <path d="m4 12.5 5 5L20 6.5" />,
  wheat: (
    <>
      <path d="M12 22V9" />
      <path d="M12 9c0-2 1.5-3.5 3.5-4c0 2-1.5 3.5-3.5 4Z" />
      <path d="M12 9C12 7 10.5 5.5 8.5 5c0 2 1.5 3.5 3.5 4Z" />
      <path d="M12 14c0-2 1.5-3.5 3.5-4c0 2-1.5 3.5-3.5 4Z" />
      <path d="M12 14c0-2-1.5-3.5-3.5-4c0 2 1.5 3.5 3.5 4Z" />
    </>
  ),
  chevron: <path d="m6 9 6 6 6-6" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 5.2a3.4 3.4 0 0 1 0 5.6" />
      <path d="M17.5 14.2A6.5 6.5 0 0 1 21.5 20" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" />
    </>
  ),
  handshake: (
    <>
      <path d="m11 17 2 2a1.7 1.7 0 0 0 2.4-2.4" />
      <path d="m13.6 14.6 2.8 2.8a1.7 1.7 0 0 0 2.4-2.4l-4.6-4.6" />
      <path d="M3 8.5 6.5 5l3 1.5L13 5l4 3.5" />
      <path d="M3 8.5 8 15a1.8 1.8 0 0 0 2.7.2L14 12" />
      <path d="M21 8.5 17 12" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.6-3.6" />
    </>
  ),
  layers: (
    <>
      <path d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path d="m3 13 9 5 9-5" />
    </>
  ),
};

function Icon({ name, className = "h-6 w-6", strokeWidth = 1.7 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {ICON_PATHS[name] || ICON_PATHS.leaf}
    </svg>
  );
}

/* --------------------------- ANIMATION HELPERS --------------------------- */

function Reveal({ children, variant = "up", delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const base =
    variant === "left"
      ? "reveal-left"
      : variant === "right"
      ? "reveal-right"
      : variant === "zoom"
      ? "reveal-zoom"
      : "reveal";
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`${base} ${shown ? "is-visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

function CountUp({ value, duration = 1600 }) {
  const m = String(value).match(/^(\D*)(\d+)(.*)$/);
  const prefix = m ? m[1] : "";
  const target = m ? parseInt(m[2], 10) : 0;
  const suffix = m ? m[3] : String(value);
  const ref = useRef(null);
  const started = useRef(false);
  const [n, setN] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || !m) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const t0 = performance.now();
            const tick = (t) => {
              const p = Math.min((t - t0) / duration, 1);
              setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        }),
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration, m]);
  return (
    <span ref={ref}>
      {prefix}
      {m ? n : ""}
      {suffix}
    </span>
  );
}

/* ------------------------------ UI ATOMS ------------------------------ */

function Eyebrow({ children, light = false, className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${
        light
          ? "border-white/30 bg-white/10 text-white"
          : "border-[#DA2128]/20 bg-[#FEF7F4] text-[#DA2128]"
      } ${className}`}
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="anim-pulse-ring absolute inline-flex h-full w-full rounded-full bg-[#F59C00]" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#F59C00]" />
      </span>
      {children}
    </span>
  );
}

function Kicker({ children, className = "" }) {
  return <Eyebrow className={className}>{children}</Eyebrow>;
}

const furrows = {
  backgroundImage: "radial-gradient(#DA2128 0.6px, transparent 0.6px)",
  backgroundSize: "24px 24px",
};

function SectionHeading({ eyebrow, title, subtitle, light = false }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <Reveal>
        <Eyebrow light={light}>{eyebrow}</Eyebrow>
      </Reveal>
      <Reveal delay={80}>
        <h2
          className={`mt-5 text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-[2.7rem] ${
            light ? "text-white" : "text-stone-900"
          }`}
        >
          {title}
        </h2>
      </Reveal>
      <Reveal delay={140}>
        <div className="mt-5 flex items-center justify-center gap-2" aria-hidden="true">
          <span className="h-[3px] w-10 rounded-full bg-[#DA2128]" />
          <span className="h-[3px] w-4 rounded-full bg-[#F59C00]" />
          <span className="h-[3px] w-2 rounded-full bg-[#DB6400]" />
        </div>
      </Reveal>
      {subtitle && (
        <Reveal delay={200}>
          <p
            className={`mt-5 text-base leading-relaxed sm:text-[17px] ${
              light ? "text-white/80" : "text-stone-600"
            }`}
          >
            {subtitle}
          </p>
        </Reveal>
      )}
    </div>
  );
}

function BtnPrimary({ children, href = "#", icon = "arrow", className = "" }) {
  return (
    <a
      href={href}
      className={`shine-parent group inline-flex items-center gap-2.5 rounded-full bg-[#DA2128] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#DA2128]/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#DB2628] hover:shadow-xl ${className}`}
    >
      <span className="relative z-10">{children}</span>
      <Icon
        name={icon}
        className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
        strokeWidth={2.2}
      />
    </a>
  );
}

function BtnAmber({ children, href = "#", icon = "download", className = "" }) {
  return (
    <a
      href={href}
      className={`shine-parent group inline-flex items-center gap-2.5 rounded-full bg-[#F59C00] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#F59C00]/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#DB6400] hover:shadow-xl ${className}`}
    >
      <span className="relative z-10">{children}</span>
      <Icon
        name={icon}
        className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5"
        strokeWidth={2.2}
      />
    </a>
  );
}

function BtnGhost({ children, href = "#", icon = "arrow", light = false }) {
  return (
    <a
      href={href}
      className={`group inline-flex items-center gap-2.5 rounded-full border px-7 py-3.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
        light
          ? "border-white/40 bg-white/10 text-white hover:border-white hover:bg-white hover:text-[#DA2128]"
          : "border-stone-300 bg-white text-stone-800 hover:border-[#DA2128] hover:text-[#DA2128]"
      }`}
    >
      {children}
      <Icon
        name={icon}
        className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
        strokeWidth={2.2}
      />
    </a>
  );
}

function FieldPattern({ className = "", opacity = 0.05 }) {
  return (
    <svg
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
      style={{ opacity }}
      aria-hidden="true"
    >
      <defs>
        <pattern id="agri-grid" width="46" height="46" patternUnits="userSpaceOnUse">
          <path d="M23 8v30" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
          <path d="M23 16c0-3 2-5 5-5.6c0 3-2 5-5 5.6Z" stroke="currentColor" strokeWidth="1.1" fill="none" />
          <path d="M23 16c0-3-2-5-5-5.6c0 3 2 5 5 5.6Z" stroke="currentColor" strokeWidth="1.1" fill="none" />
          <path d="M23 24c0-3 2-5 5-5.6c0 3-2 5-5 5.6Z" stroke="currentColor" strokeWidth="1.1" fill="none" />
          <path d="M23 24c0-3-2-5-5-5.6c0 3 2 5 5 5.6Z" stroke="currentColor" strokeWidth="1.1" fill="none" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#agri-grid)" />
    </svg>
  );
}


function Breadcrumb() {
  return (
    <nav
      aria-label="Breadcrumb"
      className="breadcrumb-in absolute left-4 top-4 z-20 sm:left-8 sm:top-6"
    >
      <ol className="flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#141419] shadow-lg shadow-black/5 backdrop-blur-md sm:text-sm">
        <li>
          <a
            href="#top"
            className="inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-[#DA2128]"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 10.5 12 3l9 7.5" />
              <path d="M5 10v10h14V10" />
              <path d="M9 20v-6h6v6" />
            </svg>
            Home
          </a>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li>
          <a href="#about" className="transition-colors duration-300 hover:text-[#DA2128]">
            Sectors
          </a>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li className="rounded-full bg-[#DA2128] px-3 py-1 text-white shadow-sm">Agriculture & Food Processing</li>
        <li className="text-black/30">&rsaquo;</li>
        <li className="rounded-full bg-[#DA2128] px-3 py-1 text-white shadow-sm">Agri Industries & Farming Practices</li>
      </ol>
    </nav>
  );
}

/* =========================== MAIN COMPONENT =========================== */

export default function SectorsPage() {
  /* nav state */
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [activeSector, setActiveSector] = useState(0);
  const [mobileSector, setMobileSector] = useState(null);

  /* page state */
  const [progress, setProgress] = useState(0);
  const [query, setQuery] = useState("");
  const [openCard, setOpenCard] = useState("agriculture-food-processing");
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    org: "",
    email: "",
    interest: INTERESTS[0],
    message: "",
  });

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 28);
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTORS;
    return SECTORS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.blurb.toLowerCase().includes(q) ||
        s.councils.some((c) => c.label.toLowerCase().includes(q))
    );
  }, [query]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 5000);
  };

  const inputCls =
    "w-full rounded-xl border border-stone-200 bg-[#FAF7F2] px-4 py-3.5 text-[14.5px] text-stone-800 outline-none transition-all duration-300 placeholder:text-stone-400 focus:border-[#DA2128] focus:bg-white focus:ring-4 focus:ring-[#DA2128]/10";

  return (
    <div className="min-h-screen bg-[#FAF7F2] antialiased">
      {/* ============================ STYLES ============================ */}
      <style>{`
        html { scroll-behavior: smooth; }
        body { background:#FAF7F2; color:#1c1917; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; -webkit-font-smoothing: antialiased; }
        ::selection { background:#DA2128; color:#fff; }
        ::-webkit-scrollbar { width:10px; height:10px; }
        ::-webkit-scrollbar-track { background:#FEF7F4; }
        ::-webkit-scrollbar-thumb { background:linear-gradient(#DA2128,#DB6400); border-radius:9999px; }
        @keyframes agri-float { 0%,100%{transform:translateY(0) rotate(0)} 50%{transform:translateY(-18px) rotate(4deg)} }
        @keyframes agri-float-slow { 0%,100%{transform:translate(0,0)} 50%{transform:translate(12px,-26px)} }
        @keyframes agri-sway { 0%,100%{transform:rotate(-6deg)} 50%{transform:rotate(6deg)} }
        @keyframes agri-marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes agri-shine { 0%{transform:translateX(-120%) skewX(-20deg)} 60%,100%{transform:translateX(240%) skewX(-20deg)} }
        @keyframes agri-pulse-ring { 0%{transform:scale(.85);opacity:.7} 70%{transform:scale(1.5);opacity:0} 100%{opacity:0} }
        @keyframes agri-spin-slow { to{transform:rotate(360deg)} }
        @keyframes agri-blob { 0%,100%{border-radius:62% 38% 46% 54% / 55% 48% 52% 45%} 50%{border-radius:42% 58% 66% 34% / 38% 62% 38% 62%} }
        .anim-float{animation:agri-float 7s ease-in-out infinite}
        .anim-float-slow{animation:agri-float-slow 11s ease-in-out infinite}
        .anim-sway{animation:agri-sway 5s ease-in-out infinite;transform-origin:bottom center}
        .anim-marquee{animation:agri-marquee 32s linear infinite}
        .anim-spin-slow{animation:agri-spin-slow 26s linear infinite}
        .anim-blob{animation:agri-blob 14s ease-in-out infinite}
        .anim-pulse-ring{animation:agri-pulse-ring 2.6s cubic-bezier(.22,.61,.36,1) infinite}
        .marquee-pause:hover .anim-marquee{animation-play-state:paused}
        .shine-parent{position:relative;overflow:hidden}
        .shine-parent::after{content:"";position:absolute;top:0;bottom:0;left:0;width:45%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.45),transparent);transform:translateX(-120%) skewX(-20deg)}
        .shine-parent:hover::after{animation:agri-shine 1s ease-out}
        .reveal{opacity:0;transform:translateY(28px);transition:opacity .7s cubic-bezier(.22,1,.36,1),transform .7s cubic-bezier(.22,1,.36,1)}
        .reveal-left{opacity:0;transform:translateX(-34px);transition:opacity .8s cubic-bezier(.22,1,.36,1),transform .8s cubic-bezier(.22,1,.36,1)}
        .reveal-right{opacity:0;transform:translateX(34px);transition:opacity .8s cubic-bezier(.22,1,.36,1),transform .8s cubic-bezier(.22,1,.36,1)}
        .reveal-zoom{opacity:0;transform:scale(.92);transition:opacity .7s cubic-bezier(.22,1,.36,1),transform .7s cubic-bezier(.22,1,.36,1)}
        .is-visible{opacity:1!important;transform:none!important}
        @media (prefers-reduced-motion: reduce){*,*::before,*::after{animation-duration:.001ms!important;animation-iteration-count:1!important;transition-duration:.001ms!important}}
      `}</style>

      {/* ======================= SCROLL PROGRESS ======================= */}
      <div className="fixed inset-x-0 top-0 z-[60] h-1">
        <div
          className="h-full bg-gradient-to-r from-[#DA2128] via-[#DB6400] to-[#F59C00] transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        className={`fixed bottom-7 right-7 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#DA2128] text-white shadow-xl shadow-[#DA2128]/35 transition-all duration-300 hover:-translate-y-1 hover:bg-[#DB6400] ${
          progress > 8 ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0"
        }`}
      >
        <Icon name="chevron" className="h-5 w-5 rotate-180" strokeWidth={2.6} />
      </button>      

      <main>
        {/* ============================== HERO ============================== */}
       <section id="home" className="relative overflow-hidden bg-stone-950">
  <Breadcrumb />
  <div className="absolute inset-0">
    <img
      src={IMAGES.field}
      alt="Aerial view of a farmer in a lush green field"
      className="h-full w-full object-cover opacity-45"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-stone-950 via-stone-950/85 to-[#DA2128]/45" />
  </div>
  <FieldPattern className="text-white" opacity={0.07} />
  <div className="anim-blob anim-float pointer-events-none absolute -right-28 top-10 h-80 w-80 bg-[#F59C00]/25 blur-3xl" />
  <div className="anim-blob anim-float-slow pointer-events-none absolute -left-24 bottom-0 h-72 w-72 bg-[#DA2128]/30 blur-3xl" />

  <div className="relative mx-auto max-w-7xl px-6 pb-14 pt-10 lg:pb-16 lg:pt-12">
    <div className="mt-6 grid items-end gap-8  justify-center mt-[10px]">
      <div className="mt-[30px]">
        <Reveal delay={60}>
         
        </Reveal>

        <Reveal delay={130}>
  <h1
    className="mt-4 text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-[2.75rem]"
    style={{ fontFamily: "Fraunces, Georgia, serif" }}
  >
    Sectors &amp;
    <span className="mt-1 block bg-gradient-to-r from-[#F59C00] via-[#DB6400] to-[#DA2128] bg-clip-text text-transparent">
      National Councils
    </span>
  </h1>
</Reveal>

        <Reveal delay={200}>
          <p className="mt-4 max-w-xl text-[14.5px] leading-relaxed text-white/85">
            Explore the verticals through which ASSOCHAM shapes India&apos;s agri-industrial agenda.
            Each sector houses dedicated national councils that convene industry, government and
            research to turn insight into policy — and policy into farm-level impact.
          </p>
        </Reveal>

        <Reveal delay={280}>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <BtnPrimary href="#sector-directory">Browse all sectors</BtnPrimary>
            <BtnGhost href="#council" light>
              Featured council
            </BtnGhost>
          </div>
        </Reveal>
      </div>

      <Reveal variant="right" delay={220}>
        {/* <div className="grid grid-cols-2 gap-3">
          {HERO_STATS.map((s) => (
            <div
              key={s.label}
              className="group rounded-xl border border-white/15 bg-white/5 p-4 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-[#F59C00]/50 hover:bg-white/10"
            >
              <Icon
                name={s.icon}
                className="h-4 w-4 text-[#F59C00] transition-transform duration-300 group-hover:scale-110"
                strokeWidth={1.9}
              />
              <p className="mt-2 text-xl font-extrabold text-white">
                <CountUp value={s.value} />
              </p>
              <p className="mt-0.5 text-[11px] leading-snug text-white/65">{s.label}</p>
            </div>
          ))}
        </div> */}
      </Reveal>
    </div>
  </div>

  <svg
    className="relative -mb-px block w-full text-[#FAF7F2]"
    viewBox="0 0 1440 60"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      d="M0,32L80,35.3C160,39,320,45,480,44C640,43,800,32,960,30C1120,28,1280,35,1360,38.7L1440,42L1440,60L0,60Z"
    />
  </svg>
</section>

        {/* ========================= FUNCTIONS OVERVIEW ========================= */}
<section
  id="functions-overview"
  className="scroll-mt-24 relative overflow-hidden bg-[#FAF7F2]"
>
  {/* Faded dot pattern – low opacity so it never sits on text */}
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0 opacity-[0.18]"
    style={{
      backgroundImage: "radial-gradient(#DA2128 1.1px, transparent 1.1px)",
      backgroundSize: "18px 18px",
    }}
  />

  <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:py-28">
    {/* Left column */}
    <div className="lg:col-span-5">
      <div className="lg:sticky lg:top-28">
        <Reveal>
          <Kicker>What We Do</Kicker>
          <h2 className="mt-4 font-[Fraunces,Georgia,serif] text-4xl font-black leading-tight text-[#2E150E] lg:text-5xl">
            Core Functions <em className="text-[#DA2128]">of the Council</em>
          </h2>
          <p className="mt-5 leading-relaxed text-stone-600">
            Our multi-pronged approach addresses the entire agricultural ecosystem — from policy formulation to
            grassroots implementation.
          </p>
        </Reveal>

        <Reveal delay={150}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-[#DA2128]/30 bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-wider text-[#DA2128]">
              06 Mandates
            </span>
            <span className="rounded-full border border-[#DB6400]/30 bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-wider text-[#DB6400]">
              01 Continuum
            </span>
          </div>

          <a
            href="#contact"
            className="group mt-8 inline-flex items-center gap-2 text-sm font-extrabold uppercase tracking-[0.18em] text-[#DB6400] transition hover:text-[#DA2128]"
          >
            Partner with us
            <IcArrow className="h-4 w-4 transition-transform group-hover:translate-x-1.5" />
          </a>
        </Reveal>

        <Wheat className="mt-10 hidden h-20 w-20 animate-[sway_5s_ease-in-out_infinite] text-[#DA2128]/20 lg:block" />
      </div>
    </div>

    {/* Right column – list */}
    <div className="lg:col-span-7">
      {FUNCTIONS.map((f, i) => (
        <Reveal key={f.title} delay={i * 90}>
          <div className="group -mx-4 flex gap-5 border-t border-[#2E150E]/10 px-4 py-7 transition-all duration-300 last:border-b hover:bg-white/90 hover:shadow-lg hover:shadow-[#3D0B0E]/5 sm:gap-7">
            {/* Number – faded */}
            <span className="font-[Fraunces,Georgia,serif] text-lg font-bold italic text-[#DB6400]/60">
              {String(i + 1).padStart(2, "0")}
            </span>

            {/* Icon */}
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-md border border-stone-300 bg-white text-[#2E150E] transition-all duration-300 group-hover:rotate-3 group-hover:border-[#DA2128] group-hover:bg-[#DA2128] group-hover:text-white">
              <f.Icon className="h-6 w-6" />
            </span>

            {/* Title + description – solid background so dots never appear on text */}
            <div className="min-w-0 flex-1 rounded-md bg-[#FAF7F2]/95 px-1 py-0.5">
              <h3 className="font-[Fraunces,Georgia,serif] text-xl font-bold text-[#2E150E]">
                {f.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{f.desc}</p>
            </div>

            <IcArrow className="ml-auto mt-1 h-5 w-5 shrink-0 -translate-x-2 text-[#DA2128] opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
          </div>
        </Reveal>
      ))}
    </div>
  </div>
</section>

        {/* ========================= HOW COUNCILS WORK ========================= */}
        <section id="how-it-works" className="relative overflow-hidden bg-white py-20 lg:py-28">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#FAF7F2] to-transparent" />
          <div className="relative mx-auto max-w-7xl px-6">
            <SectionHeading
              eyebrow="Operating Model"
              title="How an ASSOCHAM Council Works"
              subtitle="A repeatable four-stage engine that converts industry insight into measurable outcomes on the ground."
            />
            <div className="relative mt-16">
              <span className="absolute left-0 right-0 top-[46px] hidden h-[2px] bg-gradient-to-r from-[#DA2128] via-[#DB6400] to-[#F59C00] opacity-25 lg:block" />
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                {HOW_IT_WORKS.map((s, i) => (
                  <Reveal key={s.step} delay={i * 120}>
                    <div className="group relative text-center">
                      <span className="relative mx-auto flex h-24 w-24 items-center justify-center rounded-full border-4 border-white bg-[#FAF7F2] text-[#DA2128] shadow-lg shadow-stone-900/5 transition-all duration-500 group-hover:-translate-y-2 group-hover:bg-gradient-to-br group-hover:from-[#DA2128] group-hover:to-[#DB6400] group-hover:text-white group-hover:shadow-2xl group-hover:shadow-[#DA2128]/30">
                        <Icon name={s.icon} className="h-10 w-10" strokeWidth={1.5} />
                        <span className="absolute -right-1 -top-1 flex h-8 w-8 items-center justify-center rounded-full bg-[#F59C00] text-[11px] font-extrabold text-white shadow-md">
                          {s.step}
                        </span>
                      </span>
                      <h3 className="mt-6 text-[19px] font-bold text-stone-900">{s.title}</h3>
                      <p className="mx-auto mt-3 max-w-xs text-[14.5px] leading-relaxed text-stone-600">{s.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ======================== COUNCIL SPOTLIGHT ======================== */}
        <section id="council" className="relative overflow-hidden bg-gradient-to-b from-[#FEF7F4] via-[#FAF7F2] to-[#FEF7F4] py-20 lg:py-28">
          <FieldPattern className="text-[#DB6400]" opacity={0.05} />
          <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20">
            <Reveal variant="left">
              <div className="relative">
                <div className="overflow-hidden rounded-[32px] border-4 border-white shadow-2xl shadow-stone-900/15">
                  <img src={IMAGES.hero} alt="Indian farmer in a wheat field" className="h-[420px] w-full object-cover transition-transform duration-[1400ms] hover:scale-105" loading="lazy" />
                </div>
                <div className="anim-float absolute -bottom-8 -right-6 w-60 rounded-2xl bg-white p-5 shadow-2xl">
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[#DB6400]">Flagship Council</p>
                  <p className="mt-2 text-[15px] font-bold leading-snug text-stone-900">Agri Industries &amp; Farming Practices</p>
                  <div className="mt-4 flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="anim-pulse-ring absolute inline-flex h-2 w-2 rounded-full bg-[#DA2128]" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#DA2128]" />
                    </span>
                    <span className="text-[11.5px] font-semibold text-stone-500">Active · FY 2025-26</span>
                  </div>
                </div>
                <div className="anim-float-slow absolute -left-8 -top-8 hidden w-40 overflow-hidden rounded-2xl border-4 border-white shadow-xl sm:block">
                  <img src={IMAGES.sower} alt="Farmer sowing seeds" className="h-28 w-full object-cover" loading="lazy" />
                </div>
              </div>
            </Reveal>
            <div>
              <Reveal variant="right"><Eyebrow>Council Spotlight</Eyebrow></Reveal>
              <Reveal variant="right" delay={80}>
                <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-stone-900 sm:text-4xl lg:text-[2.6rem]">
                  Agri Industries &amp;{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10 text-[#DA2128]">Farming Practices</span>
                    <span className="absolute inset-x-0 bottom-1 z-0 h-3 rounded bg-[#F59C00]/25" />
                  </span>
                </h2>
              </Reveal>
              <Reveal variant="right" delay={150}>
                <p className="mt-6 text-[16px] leading-relaxed text-stone-600">
                  ASSOCHAM promulgates the agriculture sector through a balanced integration of modern
                  innovation and traditional wisdom. Chaired by <b>Mr. Jaidev R. Shroff, Global CEO of UPL
                  Limited</b>, the council drives food security, climate resilience and farmer prosperity —
                  empowering 140+ million farming families.
                </p>
              </Reveal>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {COUNCIL_STATS.map((s, i) => (
                  <Reveal key={s.value} variant="right" delay={200 + i * 80}>
                    <div className="rounded-2xl bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#DA2128]/10">
                      <p className="text-2xl font-extrabold text-[#DA2128]"><CountUp value={s.value} /></p>
                      <p className="mt-1 text-[11.5px] leading-snug text-stone-500">{s.label}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={520}>
                <div className="mt-9 flex flex-wrap gap-4">
                  <BtnPrimary href="#functions-overview">Explore initiatives</BtnPrimary>
                  <BtnGhost href="#membership" icon="handshake">Join a council</BtnGhost>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* =========================== CORE FUNCTIONS =========================== */}
        <section id="functions" className="relative overflow-hidden bg-white py-20 lg:py-28">
          <div className="relative mx-auto max-w-7xl px-6">
            <SectionHeading
              eyebrow="What We Do"
              title="Core Functions of the Council"
              subtitle="Our multi-pronged approach addresses the entire agricultural ecosystem — from policy formulation to grassroots implementation."
            />
            <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {CORE_FUNCTIONS.map((f, i) => (
                <Reveal key={f.title} delay={i * 80}>
                  <article className="group relative h-full overflow-hidden rounded-3xl border border-stone-200 bg-[#FAF7F2] p-8 transition-all duration-500 hover:-translate-y-2 hover:border-transparent hover:bg-white hover:shadow-2xl hover:shadow-[#DA2128]/10">
                    <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-[#DA2128] via-[#DB6400] to-[#F59C00] transition-transform duration-500 group-hover:scale-x-100" />
                    <span className="pointer-events-none absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-[#FEF7F4] to-[#FAF7F2] transition-transform duration-700 group-hover:scale-150" />
                    <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#DA2128] shadow-sm transition-all duration-500 group-hover:bg-gradient-to-br group-hover:from-[#DA2128] group-hover:to-[#DB6400] group-hover:text-white group-hover:shadow-lg group-hover:shadow-[#DA2128]/30">
                      <Icon name={f.icon} className="h-7 w-7" strokeWidth={1.7} />
                    </span>
                    <h3 className="relative mt-6 text-[18px] font-bold leading-snug text-stone-900">{f.title}</h3>
                    <p className="relative mt-3 text-[14.5px] leading-relaxed text-stone-600">{f.text}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ FOCUS AREAS ============================ */}
        <section id="focus-areas" className="relative overflow-hidden bg-gradient-to-b from-[#FEF7F4] via-[#FAF7F2] to-[#FEF7F4] py-20 lg:py-28">
          <FieldPattern className="text-[#DB6400]" opacity={0.05} />
          <div className="relative mx-auto max-w-7xl px-6">
            <SectionHeading
              eyebrow="Key Priorities"
              title="Strategic Focus Areas"
              subtitle="We prioritize interventions that create scalable, lasting impact across India's agri value chain."
            />
            <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {FOCUS_AREAS.map((a, i) => (
                <Reveal key={a.title} variant="zoom" delay={i * 90}>
                  <article className="group relative flex h-full flex-col overflow-hidden rounded-3xl bg-white p-8 shadow-[0_2px_20px_-12px_rgba(28,25,23,0.35)] transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#DA2128]/15">
                    <span className="absolute inset-0 bg-gradient-to-br from-[#DA2128] to-[#DB6400] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FEF7F4] to-[#FAF7F2] text-[#DA2128] transition-all duration-500 group-hover:rotate-6 group-hover:from-white/20 group-hover:to-white/20 group-hover:text-white">
                      <Icon name={a.icon} className="h-8 w-8" strokeWidth={1.6} />
                    </span>
                    <h3 className="relative z-10 mt-6 text-[19px] font-bold leading-snug text-stone-900 transition-colors duration-500 group-hover:text-white">
                      {a.title}
                    </h3>
                    <p className="relative z-10 mt-3 flex-1 text-[14.5px] leading-relaxed text-stone-600 transition-colors duration-500 group-hover:text-white/90">
                      {a.text}
                    </p>
                    <a href="#contact" className="relative z-10 mt-6 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-wide text-[#DA2128] transition-colors duration-500 group-hover:text-white">
                      Learn more
                      <span className="flex h-7 w-7 items-center justify-center rounded-full border border-current transition-transform duration-300 group-hover:translate-x-1">
                        <Icon name="arrow" className="h-3.5 w-3.5" strokeWidth={2.4} />
                      </span>
                    </a>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ============================== AGRI TECH ============================== */}
        <section id="agri-tech" className="relative overflow-hidden bg-stone-950 py-20 lg:py-28">
          <div className="absolute inset-0">
            <img src={IMAGES.drone} alt="Precision farming with drone technology" className="h-full w-full object-cover opacity-30" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/90 to-[#DA2128]/50" />
          </div>
          <FieldPattern className="text-white" opacity={0.06} />
          <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20">
            <Reveal variant="left">
              <div className="relative">
                <div className="overflow-hidden rounded-[32px] border border-white/20 shadow-2xl shadow-black/50">
                  <img src={IMAGES.drone} alt="Agricultural drone spraying crops" className="h-[380px] w-full object-cover transition-transform duration-[1400ms] hover:scale-110" loading="lazy" />
                </div>
                <div className="anim-float absolute -bottom-8 -right-6 w-56 rounded-2xl bg-white/95 p-5 shadow-2xl backdrop-blur">
                  <div className="flex items-center gap-3">
                    <span className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#F59C00] to-[#DB6400] text-white">
                      <Icon name="drone" className="h-5 w-5" strokeWidth={1.9} />
                    </span>
                    <div>
                      <p className="text-[13px] font-bold text-stone-900">Kisan Drones</p>
                      <p className="text-[11.5px] text-stone-500">Precision at scale</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
            <div>
              <Reveal variant="right">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur">
                  <Icon name="bulb" className="h-3.5 w-3.5 text-[#F59C00]" strokeWidth={2} />
                  Agri-Tech Transformation
                </span>
              </Reveal>
              <Reveal variant="right" delay={80}>
                <h2 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.6rem]">
                  Bridging the Digital Divide for{" "}
                  <span className="bg-gradient-to-r from-[#F59C00] to-[#DB6400] bg-clip-text text-transparent">140 Million Farmers</span>
                </h2>
              </Reveal>
              <Reveal variant="right" delay={150}>
                <p className="mt-6 text-[16px] leading-relaxed text-white/80">
                  86% of India&apos;s farmers remain beyond the reach of mainstream agri-tech solutions. The
                  council champions a unified ecosystem — research institutions, startups, government and
                  data platforms — to validate, scale and deliver technology to every farm.
                </p>
              </Reveal>
              <div className="mt-10 grid grid-cols-3 gap-4">
                {[
                  { value: "86%", label: "Farmers outside agri-tech reach" },
                  { value: "2x", label: "Farmer income target by 2030" },
                  { value: "5000+", label: "Agri-startups in India" },
                ].map((s, i) => (
                  <Reveal key={s.label} delay={200 + i * 100}>
                    <div className="rounded-2xl border border-white/15 bg-white/5 p-5 text-center backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-[#F59C00]/50 hover:bg-white/10">
                      <p className="text-2xl font-extrabold text-[#F59C00] sm:text-3xl"><CountUp value={s.value} /></p>
                      <p className="mt-2 text-[11.5px] leading-snug text-white/70">{s.label}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={420}>
                <div className="mt-10 flex flex-wrap gap-4">
                  <BtnAmber href="#publications">Download EY Report</BtnAmber>
                  <BtnGhost href="#contact" light>Partner with us</BtnGhost>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ============================ STAKEHOLDERS ============================ */}
        <section id="stakeholders" className="relative overflow-hidden bg-white py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6">
            <SectionHeading
              eyebrow="Ecosystem Partners"
              title="Key Stakeholders"
              subtitle="The council works in close collaboration with central ministries, regulatory bodies, research institutions, and industry partners."
            />
          </div>
          <div className="marquee-pause relative mt-14 overflow-hidden">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-white to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-white to-transparent" />
            <div className="anim-marquee flex w-max">
              {[...STAKEHOLDERS, ...STAKEHOLDERS].map((s, i) => (
                <div
                  key={`${s.short}-${i}`}
                  className="group mx-3 flex w-[280px] shrink-0 items-center gap-4 rounded-2xl border border-stone-200 bg-white px-5 py-4 transition-all duration-300 hover:-translate-y-1 hover:border-[#DA2128]/40 hover:shadow-xl hover:shadow-[#DA2128]/10"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#FEF7F4] to-[#FAF7F2] text-[12px] font-extrabold text-[#DA2128] transition-all duration-300 group-hover:from-[#DA2128] group-hover:to-[#DB6400] group-hover:text-white">
                    {s.short}
                  </span>
                  <p className="text-[12.5px] font-semibold leading-snug text-stone-700">{s.name}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="mx-auto mt-14 max-w-7xl px-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {STAKEHOLDERS.map((s, i) => (
                <Reveal key={s.short} delay={(i % 3) * 90}>
                  <div className="group flex h-full items-center gap-4 rounded-2xl bg-[#FAF7F2] px-5 py-4 transition-all duration-300 hover:bg-[#FEF7F4]">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-[11.5px] font-extrabold text-[#DA2128] shadow-sm transition-transform duration-300 group-hover:scale-110">
                      {s.short}
                    </span>
                    <p className="text-[13px] font-semibold leading-snug text-stone-700">{s.name}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* =============================== EVENTS =============================== */}
        <section id="events" className="relative overflow-hidden bg-[#FAF7F2] py-20 lg:py-28">
          <FieldPattern className="text-[#DA2128]" opacity={0.04} />
          <div className="relative mx-auto max-w-7xl px-6">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-2xl">
                <Reveal variant="left"><Eyebrow>Engagements &amp; Events</Eyebrow></Reveal>
                <Reveal variant="left" delay={80}>
                  <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-stone-900 sm:text-4xl lg:text-[2.6rem]">
                    Recent &amp; Upcoming Initiatives
                  </h2>
                </Reveal>
              </div>
              <Reveal variant="right" delay={120}><BtnGhost href="#contact">View all events</BtnGhost></Reveal>
            </div>
            <div className="relative mt-16">
              <span className="absolute left-[27px] top-2 hidden h-[calc(100%-2rem)] w-[2px] bg-gradient-to-b from-[#DA2128] via-[#DB6400] to-transparent md:block" />
              <div className="space-y-8">
                {EVENTS.map((e, i) => (
                  <Reveal key={e.title} delay={i * 120}>
                    <div className="group relative md:pl-20">
                      <span className="absolute left-0 top-8 hidden h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-lg shadow-stone-900/5 ring-1 ring-stone-200 transition-all duration-500 group-hover:bg-gradient-to-br group-hover:from-[#DA2128] group-hover:to-[#DB6400] group-hover:ring-transparent md:flex">
                        <Icon name="calendar" className="h-6 w-6 text-[#DA2128] transition-colors duration-500 group-hover:text-white" strokeWidth={1.8} />
                      </span>
                      <article className="relative overflow-hidden rounded-3xl border border-stone-200 bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:border-[#DA2128]/30 hover:shadow-2xl hover:shadow-[#DA2128]/10 sm:p-9">
                        <span className="absolute inset-y-0 left-0 w-1.5 origin-top scale-y-0 bg-gradient-to-b from-[#DA2128] to-[#F59C00] transition-transform duration-500 group-hover:scale-y-100" />
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="rounded-full bg-[#DA2128] px-3.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-white">{e.tag}</span>
                          <span className="rounded-full bg-[#FEF7F4] px-3.5 py-1 text-[11.5px] font-semibold text-[#DB6400]">{e.date}</span>
                        </div>
                        <h3 className="mt-5 text-[21px] font-bold leading-snug text-stone-900 transition-colors group-hover:text-[#DA2128] sm:text-2xl">{e.title}</h3>
                        <p className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-stone-500">
                          <Icon name="pin" className="h-4 w-4 text-[#DB6400]" strokeWidth={1.9} />
                          {e.place}
                        </p>
                        <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-stone-600">{e.text}</p>
                        <div className="mt-5 flex items-start gap-3 rounded-2xl bg-[#FAF7F2] p-4">
                          <Icon name="check" className="mt-0.5 h-4 w-4 shrink-0 text-[#DA2128]" strokeWidth={2.8} />
                          <p className="text-[13.5px] font-medium leading-relaxed text-stone-700">{e.highlight}</p>
                        </div>
                      </article>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============================ PUBLICATIONS ============================ */}
        <section id="publications" className="relative overflow-hidden bg-gradient-to-b from-white via-[#FEF7F4] to-[#FAF7F2] py-20 lg:py-28">
          <div className="relative mx-auto max-w-7xl px-6">
            <SectionHeading
              eyebrow="Knowledge Hub"
              title="Publications &amp; Reports"
              subtitle="Evidence-based research produced in partnership with leading consulting firms and knowledge partners."
            />
            <div className="mt-16 grid gap-7 lg:grid-cols-3">
              {PUBLICATIONS.map((p, i) => (
                <Reveal key={p.title} delay={i * 110}>
                  <article className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-stone-200 bg-white transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#DA2128]/10">
                    <div className="relative h-40 overflow-hidden bg-gradient-to-br from-[#DA2128] via-[#DB2628] to-[#DB6400]">
                      <svg className="absolute inset-0 h-full w-full text-white/20" viewBox="0 0 400 160" preserveAspectRatio="none" aria-hidden="true">
                        <path d="M0 120 Q60 90 120 118 T240 116 T400 96 L400 160 L0 160Z" fill="currentColor" />
                        <path d="M0 138 Q80 112 160 136 T320 130 T400 124 L400 160 L0 160Z" fill="currentColor" opacity="0.6" />
                      </svg>
                      <Icon name="wheat" className="anim-sway absolute right-6 top-6 h-16 w-16 text-white/30" strokeWidth={1.3} />
                      <div className="absolute left-6 top-6 flex flex-col gap-2">
                        <span className="w-max rounded-full bg-white/20 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white backdrop-blur">{p.date}</span>
                        <span className="w-max rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#DA2128]">{p.partner}</span>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col p-7">
                      <h3 className="text-[18px] font-bold leading-snug text-stone-900 transition-colors group-hover:text-[#DA2128]">{p.title}</h3>
                      <p className="mt-3 flex-1 text-[14.5px] leading-relaxed text-stone-600">{p.text}</p>
                      <a href="#contact" className="mt-6 inline-flex w-max items-center gap-2 rounded-full border border-[#DA2128]/25 bg-[#FEF7F4] px-5 py-2.5 text-[13px] font-bold text-[#DA2128] transition-all duration-300 hover:bg-[#DA2128] hover:text-white">
                        Download PDF
                        <Icon name="download" className="h-4 w-4" strokeWidth={2.2} />
                      </a>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
            <Reveal variant="zoom" delay={120}>
              <figure className="relative mt-20 overflow-hidden rounded-[32px] bg-gradient-to-br from-[#DA2128] via-[#DB2628] to-[#DB6400] p-10 text-white shadow-2xl shadow-[#DA2128]/25 sm:p-14">
                <span className="pointer-events-none absolute -right-10 -top-16 select-none text-[220px] font-black leading-none text-white/10">&rdquo;</span>
                <Icon name="wheat" className="anim-float pointer-events-none absolute -bottom-6 left-8 h-32 w-32 text-white/10" strokeWidth={1} />
                <blockquote className="relative max-w-4xl text-[20px] font-medium leading-relaxed sm:text-[26px] sm:leading-[1.45]">
                  &ldquo;It is time we reimagine agriculture not just as a means of livelihood but as a dynamic,
                  value-generating industry that powers India&apos;s economic growth.&rdquo;
                </blockquote>
                <figcaption className="relative mt-9 flex items-center gap-4">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-[15px] font-extrabold text-[#DA2128]">RS</span>
                  <div>
                    <p className="text-[16px] font-bold">Dr. Rangaiah V Setlem</p>
                    <p className="text-[13px] text-white/80">Chair, Agriculture &amp; Food Processing Council (Southern Region)</p>
                    <p className="text-[13px] text-white/70">Founder &amp; CEO, Womenova Agro Food Park Pvt. Ltd.</p>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </section>

        {/* ============================= MEMBERSHIP ============================= */}
        <section id="membership" className="relative overflow-hidden bg-stone-950 py-20 lg:py-28">
          <FieldPattern className="text-white" opacity={0.06} />
          <div className="anim-blob anim-float pointer-events-none absolute -right-24 top-0 h-80 w-80 bg-[#DA2128]/30 blur-3xl" />
          <div className="anim-blob anim-float-slow pointer-events-none absolute -left-24 bottom-0 h-72 w-72 bg-[#DB6400]/25 blur-3xl" />
          <div className="relative mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <div>
              <Reveal variant="left">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur">
                  <Icon name="handshake" className="h-3.5 w-3.5 text-[#F59C00]" strokeWidth={2} />
                  Council Membership
                </span>
              </Reveal>
              <Reveal variant="left" delay={80}>
                <h2 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.6rem]">
                  A seat at the table where{" "}
                  <span className="bg-gradient-to-r from-[#F59C00] to-[#DB6400] bg-clip-text text-transparent">agri policy is written</span>
                </h2>
              </Reveal>
              <Reveal variant="left" delay={150}>
                <p className="mt-6 text-[16px] leading-relaxed text-white/75">
                  Membership connects your organisation to ministries, regulators, research institutes and
                  the wider agri value chain — across every sector vertical.
                </p>
              </Reveal>
              <Reveal delay={230}>
                <div className="mt-9 flex flex-wrap gap-4">
                  <BtnAmber href="#contact" icon="arrow">Request membership</BtnAmber>
                  <BtnGhost href="#sector-directory" light>Compare sectors</BtnGhost>
                </div>
              </Reveal>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {MEMBERSHIP_BENEFITS.map((b, i) => (
                <Reveal key={b} delay={i * 90}>
                  <div className="group flex h-full items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-[#F59C00]/50 hover:bg-white/10">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#DA2128]/25 text-[#F59C00] transition-transform duration-300 group-hover:scale-110">
                      <Icon name="check" className="h-4 w-4" strokeWidth={3} />
                    </span>
                    <p className="text-[13.5px] leading-relaxed text-white/80">{b}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* =============================== CONTACT =============================== */}
        <section id="contact" className="relative overflow-hidden bg-[#FEF7F4] py-20 lg:py-28">
          <FieldPattern className="text-[#DA2128]" opacity={0.05} />
          <div className="relative mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
            <div>
              <Reveal variant="left"><Eyebrow>Get Involved</Eyebrow></Reveal>
              <Reveal variant="left" delay={80}>
                <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-stone-900 sm:text-4xl lg:text-[2.6rem]">
                  Partner with us to shape India&apos;s <span className="text-[#DA2128]">agricultural future</span>
                </h2>
              </Reveal>
              <Reveal variant="left" delay={150}>
                <p className="mt-6 text-[16px] leading-relaxed text-stone-600">
                  Whether you are an agri-business, policymaker, researcher, startup, or farmer organization —
                  join the council to contribute to India&apos;s journey toward inclusive, sustainable, and
                  technology-enabled agriculture.
                </p>
              </Reveal>
              <div className="mt-10 space-y-4">
                {[
                  { icon: "mail", label: "Email the Council", value: "d.s.rajora@assocham.com", href: "mailto:d.s.rajora@assocham.com" },
                  { icon: "phone", label: "Call us", value: "+91-11-4665 2222", href: "tel:+911146652222" },
                  { icon: "pin", label: "Visit us", value: "ASSOCHAM House, 49-50, Rani Jhansi Road, New Delhi - 110055", href: "#contact" },
                ].map((c, i) => (
                  <Reveal key={c.label} variant="left" delay={200 + i * 90}>
                    <a href={c.href} className="group flex items-start gap-4 rounded-2xl border border-stone-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#DA2128]/40 hover:shadow-xl hover:shadow-[#DA2128]/10">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#FEF7F4] text-[#DA2128] transition-all duration-300 group-hover:bg-gradient-to-br group-hover:from-[#DA2128] group-hover:to-[#DB6400] group-hover:text-white">
                        <Icon name={c.icon} className="h-5 w-5" strokeWidth={1.9} />
                      </span>
                      <span>
                        <span className="block text-[11.5px] font-bold uppercase tracking-[0.14em] text-stone-400">{c.label}</span>
                        <span className="mt-1 block text-[14.5px] font-semibold leading-snug text-stone-800">{c.value}</span>
                      </span>
                    </a>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal variant="right" delay={120}>
              <div className="relative overflow-hidden rounded-[28px] border border-stone-200 bg-white p-8 shadow-2xl shadow-stone-900/5 sm:p-10">
                <span className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#DA2128] via-[#DB6400] to-[#F59C00]" />
                <h3 className="text-[22px] font-bold text-stone-900">Send us a message</h3>
                <form className="mt-7 space-y-5" onSubmit={submit}>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block text-[12.5px] font-bold uppercase tracking-[0.1em] text-stone-500">Full Name</span>
                      <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-[12.5px] font-bold uppercase tracking-[0.1em] text-stone-500">Organization</span>
                      <input value={form.org} onChange={set("org")} className={inputCls} placeholder="Company / Institution" />
                    </label>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block text-[12.5px] font-bold uppercase tracking-[0.1em] text-stone-500">Email</span>
                      <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-[12.5px] font-bold uppercase tracking-[0.1em] text-stone-500">Area of Interest</span>
                      <select value={form.interest} onChange={set("interest")} className={inputCls}>
                        {INTERESTS.map((o) => <option key={o}>{o}</option>)}
                      </select>
                    </label>
                  </div>
                  <label className="block">
                    <span className="mb-2 block text-[12.5px] font-bold uppercase tracking-[0.1em] text-stone-500">Message</span>
                    <textarea rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us how you'd like to engage..." />
                  </label>
                  <button
                    type="submit"
                    className="shine-parent group flex w-full items-center justify-center gap-2.5 rounded-full bg-[#DA2128] px-7 py-4 text-[15px] font-bold text-white shadow-lg shadow-[#DA2128]/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#DB2628] hover:shadow-xl"
                  >
                    <span className="relative z-10">{sent ? "Enquiry Received" : "Submit Enquiry"}</span>
                    <Icon name={sent ? "check" : "arrow"} className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.4} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-500 ${sent ? "max-h-24 opacity-100" : "max-h-0 opacity-0"}`}>
                    <p className="flex items-center gap-2 rounded-xl bg-[#FAF7F2] px-4 py-3 text-[13.5px] font-medium text-stone-700">
                      <Icon name="check" className="h-4 w-4 text-[#DA2128]" strokeWidth={2.8} />
                      Thank you — the council secretariat will get back to you shortly.
                    </p>
                  </div>
                </form>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </div>
  );
}
